import { ApplicationDefaults } from '@/constants/ApplicationDefaults';
import { FACTOR_DECOMPOSITION_PDF_FACTOR_LIMIT, MAX_NUM_RISK_METRICS_TABLE_IN_PER_PAGE } from '@/constants/pdf';
import type { IPortfolioTreeSubportfolio, IPortfolioTreeStrategyStaticInfo } from '@/types/IPortfolioTree';
import { isPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';
import type { IItemUnderAnalysis } from '@/types/IItemUnderAnalysis';
import type { ComputedRef } from 'vue';
import { computed, watch } from 'vue';
import useFxConversion from './useFxConversion';
import { useBrowserLocation } from '@vueuse/core';
import useTranslation from './useTranslation';
import { hasCurrencyConversion } from '@/types/CurrencyConversion';
import { min, sumBy } from 'lodash';
import type { RegressionReturnPeriodOptions } from '@/constants/RegressionReturnPeriodOptions';
import useAnalysisModes from '@/composables/useAnalysisModes';
import { useGetColorMapIds } from './useGetColorMapIds';
import { SettingsModule } from '@/store/modules/settings';
import getEnv from '../utils/getEnv';
import { FactsheetChartTypes } from '@/types/ChartTypes';
import type { Period } from '@/types/period';
import type { FactsheetPdfParams } from '@/types/FactsheetPdfParams';
import { isFactsheetActiveReturnPdfParams } from '@/types/FactsheetPdfParams';
import { useBenchmark } from './useBenchmark';
import { ActiveReturnMode } from '@/types/ActiveReturnMode';
import type { StrategyFactsheetBasicInfoDTO } from '@/api-v2/web/strategies';
import type { IStrategy } from '@/types/strategy';
import { useAiReportState } from '@/no-code-dashboard/composables/useAiReportState';
import { getIsPdf } from '@/utils/getIsPdf';

export { getIsPdf } from '@/utils/getIsPdf';

/**
 * https://github.com/puppeteer/puppeteer/blob/main/docs/api.md#pagepdfoptions
 */
type PrintToPdfOptions = {
  format?: string;
  margin?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
  headerTemplate?: string;
  footerTemplate?: string;
};

export function useIsPdf(): ComputedRef<boolean> {
  const location = useBrowserLocation();

  return computed((): boolean => getIsPdf(location.value.href?.toString()));
}

/**
 * Turn this boolean to true to test PDFs in the browser
 * If it's false and you are on local, instead it will generate
 * the PDF from UAT and return it to you.
 *
 * should be `false` in cypress environment as we need to test the download pdf feature.
 */
const shouldTestPdfInBrowser = !window.Cypress;

export function usePdfPreferences(): {
  sectionHeaderTextColor: ComputedRef<{ color: string } | null>;
  sectionDividerColor: ComputedRef<string>;
  isHsbcPdf: ComputedRef<boolean>;
} {
  const isActuallyPdf = useIsPdf();
  const { shouldShowPdfPreview } = useAiReportState();
  const isPdf = computed((): boolean => isActuallyPdf.value || shouldShowPdfPreview.value);
  const { isHSBCEnvironment } = getEnv();

  const isHsbcPdf = computed((): boolean => isPdf.value && isHSBCEnvironment);

  const sectionHeaderTextColor = computed((): { color: string } | null =>
    isPdf.value ? { color: `${SettingsModule.pdfSectionHeaderTextColor} !important` } : null,
  );

  const sectionDividerColor = computed((): string =>
    isPdf.value ? SettingsModule.pdfSectionDividerColor : ApplicationDefaults.colors.Info,
  );

  return {
    sectionHeaderTextColor,
    sectionDividerColor,
    isHsbcPdf,
  };
}

const getItemToUse = (
  item: IItemUnderAnalysis | null,
): IPortfolioTreeSubportfolio | IPortfolioTreeStrategyStaticInfo | StrategyFactsheetBasicInfoDTO | undefined => {
  if (!item) return undefined;

  if (isPortfolioTreeSubportfolio(item)) return item;

  return item.strategy;
};

const generateAnalysisItemAttributes = (
  item: IItemUnderAnalysis | null,
  /**
   * Set this to true for pages not using the navigation panel and not saving the portfolio draft
   */
  isLegacyApiPage?: boolean,
  shouldShowFxType: boolean = true,
): string | undefined => {
  const { toCurrency, fxType } = useFxConversion();
  const { translate } = useTranslation();

  let assetClass = '';
  let factor = '';
  let currency = toCurrency.value || '';
  let fxHedging = fxType.value || '';

  const itemToUse: Partial<IItemUnderAnalysis | IStrategy> | undefined = getItemToUse(item);
  if (!itemToUse) return;

  assetClass = itemToUse.assetClass || '';
  factor = itemToUse.factor || '';

  // Allows for the pdf to use the itemUnderAnalysis' toCurrency and fxType
  // since the factor decomposition page can create a pdf on subportfolio or strategy
  // Otherwise, it will always use the root portfolio
  // Which is not applicable in PCA yet, since we are not using portfolio-trees there
  if (!isLegacyApiPage && hasCurrencyConversion(itemToUse)) {
    currency = itemToUse.toCurrency;
    fxHedging = itemToUse.fxType;
  }

  return `${translate({
    path: 'GLOBAL.ASSET_CLASS_CONSTANTS',
    item: assetClass,
  })} | ${translate({
    path: 'GLOBAL.FACTOR_CONSTANTS',
    item: factor,
  })} | ${currency} ${
    shouldShowFxType
      ? translate({
          path: 'GLOBAL.FX_CONVERSION',
          item: fxHedging,
        })
      : ''
  }`;
};

/**
 * Default pdf options to send to the sls-chromeless project
 *
 * FOR THE WHITELABEL BRANCHES, WE MAY NEED TO HAVE THIS INSTEAD:
 *  {
 *    footer: ''
 *    margin: {
 *      top: 0,
 *      bottom: 0,
 *    },
 *  }
 *
 */
export function useDefaultPdfOptions(): PrintToPdfOptions {
  return {
    margin: {
      top: 0,
      bottom: 0,
    },
  };
}

type FactorDecompPdfGraphChunk = {
  shownWithRegimes: boolean;
  shownWithoutRegimes: boolean;
  numGraphs: number;
};

const getNumGraphsForFactorDecomposition = (numRegressors: number, isRegimeEnabled: boolean): number => {
  // take the smaller of these two numbers as the number of regressor graphs drawn
  const numRegressorGraphsDrawn: FactorDecompPdfGraphChunk = {
    shownWithoutRegimes: true,
    shownWithRegimes: false,
    numGraphs: min([numRegressors, FACTOR_DECOMPOSITION_PDF_FACTOR_LIMIT]) ?? 0,
  };

  // Don't show the waterfall graph if the number of regressors exceeds `FACTOR_DECOMPOSITION_PDF_FACTOR_LIMIT`
  const numWaterfallGraphsDrawn: FactorDecompPdfGraphChunk = {
    shownWithRegimes: false,
    shownWithoutRegimes: true,
    numGraphs: numRegressors < FACTOR_DECOMPOSITION_PDF_FACTOR_LIMIT ? 2 : 0,
  };

  const otherGraphs: FactorDecompPdfGraphChunk = {
    numGraphs:
      // regression track and cumulative residual page
      2 +
      // regression return monthly and annual page
      2,
    shownWithoutRegimes: true,
    shownWithRegimes: true,
  };

  const convexityPageNoRegimes: FactorDecompPdfGraphChunk = {
    shownWithoutRegimes: true,
    shownWithRegimes: false,
    numGraphs:
      // convexity row
      1 +
      // qqplot and return distribution row
      2,
  };

  // convexity and return distribution
  const convexityPageWithRegimes: FactorDecompPdfGraphChunk = {
    shownWithRegimes: true,
    shownWithoutRegimes: false,
    numGraphs:
      // convexity row
      1 +
      // return distribution graph with different datasets
      2,
  };

  const qqPlotPageWithRegimesOnly: FactorDecompPdfGraphChunk = {
    shownWithRegimes: true,
    shownWithoutRegimes: false,
    numGraphs: 3,
  };

  const rollingPage: FactorDecompPdfGraphChunk = {
    shownWithoutRegimes: true,
    shownWithRegimes: true,
    numGraphs: 1,
  };

  return sumBy(
    [
      numRegressorGraphsDrawn,
      numWaterfallGraphsDrawn,
      otherGraphs,
      convexityPageNoRegimes,
      convexityPageWithRegimes,
      qqPlotPageWithRegimesOnly,
      rollingPage,
    ].filter(
      (p: FactorDecompPdfGraphChunk) =>
        (isRegimeEnabled && p.shownWithRegimes) || (!isRegimeEnabled && p.shownWithoutRegimes),
    ),
    'numGraphs',
  );
};

const getRollingRegressionGraphTitle = ({
  rollingRegressionOptionPeriod,
  forceActiveReturnMode,
}: {
  rollingRegressionOptionPeriod: RegressionReturnPeriodOptions;
  forceActiveReturnMode?: ActiveReturnMode;
}): string => {
  const { getActiveReturnModeText } = useAnalysisModes();
  const activeReturnModeText = computed(() => getActiveReturnModeText(forceActiveReturnMode));
  const { translate } = useTranslation();

  return `${translate({
    path: 'FACTOR_DECOMPOSITION.REGRESSION_PDF.ROLLING_REGRESSION_GRAPH_TITLE',
    params: { optionPeriod: rollingRegressionOptionPeriod, returnType: activeReturnModeText.value },
  })}`;
};

const getNumGraphsForPortfolioFactsheet = (
  numOfThresholds: number,
  shouldShowActiveReturn: boolean,
  isRegimeAnalysisActive: boolean,
): number => {
  const base =
    // portfolio weighting charts x2
    2 +
    // track record (always shown)
    1 +
    // graph sections x4 (with 2 charts each)
    8 +
    // allocation model because it takes a long time somehow
    1 +
    // risk metrics table
    1;

  if (shouldShowActiveReturn) {
    return (
      // portfolio weighting charts x2
      2 +
      // 2 track record, one for return mode and one for active return mode
      2 +
      // graph sections x4 (with 2 charts each)
      8 +
      // allocation model because it takes a long time somehow
      1
      // no need to account for more risk metrics tables because 1 is accounted for in `base`,
      // and all queries are centralized
    );
  }

  if (isRegimeAnalysisActive) {
    return (
      base +
      // n RegimeTrackRecord where n is the number of thresholds
      // and n RegimeRiskMetricsTablePdf
      numOfThresholds +
      // risk metrics table
      1
    );
  }

  return base;
};

const getNumGraphsForStrategyFactsheet = (
  numOfThresholds: number,
  shouldShowActiveReturn: boolean,
  isRegimeAnalysisActive: boolean,
): number => {
  const base =
    // track record (always shown)
    1 +
    // FactsheetChartTypes.METRICS, FactsheetChartTypes.DRAWDOWN,
    // FactsheetChartTypes.ROLLING_VOLATILITY, FactsheetChartTypes.CORRELATION
    // FactsheetChartTypes.ROLLING_SHARPE, FactsheetChartTypes.ROLLING_CORRELATION,
    6 +
    // risk metrics table
    1;

  if (shouldShowActiveReturn) {
    return (
      base +
      // active return track
      1 +
      // active return graph section with two charts
      2
      // no need to account for more risk metrics tables because 1 is accounted for in `base`,
      // and all queries are centralized
    );
  }

  if (isRegimeAnalysisActive) {
    return (
      base +
      // n RegimeTrackRecord where n is the number of thresholds
      numOfThresholds +
      // risk metrics table
      1
    );
  }

  return base;
};

const activeReturnGraphsOnFactsheet = [
  // Active Return Mode PDF Charts Page 1
  FactsheetChartTypes.METRICS,
  FactsheetChartTypes.DRAWDOWN,
  FactsheetChartTypes.RETURN_REGRESSION,
  FactsheetChartTypes.MONTHLY_RETURN_DISTRIBUTION,

  // Active Return Mode PDF Charts Page 2
  FactsheetChartTypes.ROLLING_VOLATILITY,
  FactsheetChartTypes.ROLLING_SHARPE,
  FactsheetChartTypes.ROLLING_CORRELATION,
  FactsheetChartTypes.CORRELATION,
];

const getPeriodsShownOnRiskMetricsTablePdfPage = (riskMetricPeriods: Period[]): Period[][] => {
  const sortedPeriods = Object.values(riskMetricPeriods).sort((a, b): number => (a.fromDate < b.fromDate ? -1 : 1));

  if (sortedPeriods.length <= MAX_NUM_RISK_METRICS_TABLE_IN_PER_PAGE) return [sortedPeriods];

  return [
    sortedPeriods.slice(0, MAX_NUM_RISK_METRICS_TABLE_IN_PER_PAGE),
    sortedPeriods.slice(MAX_NUM_RISK_METRICS_TABLE_IN_PER_PAGE, sortedPeriods.length),
  ];
};

const setActiveReturnParametersForFactsheet = (pdfParams: FactsheetPdfParams): void => {
  if (isFactsheetActiveReturnPdfParams(pdfParams)) {
    const shouldShowActiveReturn = isFactsheetActiveReturnPdfParams(pdfParams);

    const { benchmark } = useBenchmark();
    const { activeReturnTrackColorMapId } = useGetColorMapIds({ benchmark });

    const { changeActiveReturnMode, activeReturnRebalancingType } = useAnalysisModes();

    /**
     * It's important we only changeActiveReturnMode from StrategyFactsheetPdf or PortfolioFactsheetPdf and NOT from
     * StrategyFactsheetBasePdf or PortfolioFactsheetBasePdf.
     *
     * In the Combined Module PDFs we need the Factsheet section be showing Total or Active Return,
     * but the Factor Decomposition section always be using Excess Return (unless Regime Analysis is active,
     * in which case we use Total Return)
     */
    // eslint-disable-next-line no-restricted-syntax
    watch(
      activeReturnTrackColorMapId,
      (newActiveReturnId): void => {
        if (shouldShowActiveReturn && newActiveReturnId) {
          changeActiveReturnMode(ActiveReturnMode.ACTIVE_RETURN);
          activeReturnRebalancingType.value = pdfParams.activeReturnRebalancingType;
        }
      },
      { immediate: true },
    );
  }
};

export default function (): {
  shouldTestPdfInBrowser: boolean;
  generateAnalysisItemAttributes: (
    item: IItemUnderAnalysis,
    isLegacyApiPage?: boolean,
    shouldShowFxType?: boolean,
  ) => string | undefined;
  getNumGraphsForFactorDecomposition: (numRegressors: number, isRegimeEnabled: boolean) => number;
  getNumGraphsForPortfolioFactsheet: (
    numOfThresholds: number,
    shouldShowActiveReturn: boolean,
    isRegimeAnalysisActive: boolean,
  ) => number;
  getNumGraphsForStrategyFactsheet: (
    numOfThresholds: number,
    shouldShowActiveReturn: boolean,
    isRegimeAnalysisActive: boolean,
  ) => number;
  getRollingRegressionGraphTitle: ({
    rollingRegressionOptionPeriod,
    forceActiveReturnMode,
  }: {
    rollingRegressionOptionPeriod: RegressionReturnPeriodOptions;
    forceActiveReturnMode?: ActiveReturnMode;
  }) => string;
  activeReturnGraphsOnFactsheet: FactsheetChartTypes[];
  getPeriodsShownOnRiskMetricsTablePdfPage: (riskMetricPeriods: Period[]) => Period[][];
  setActiveReturnParametersForFactsheet: (pdfParams: FactsheetPdfParams) => void;
} {
  return {
    shouldTestPdfInBrowser,
    generateAnalysisItemAttributes,
    getNumGraphsForFactorDecomposition,
    getNumGraphsForPortfolioFactsheet,
    getNumGraphsForStrategyFactsheet,
    getRollingRegressionGraphTitle,
    activeReturnGraphsOnFactsheet,
    getPeriodsShownOnRiskMetricsTablePdfPage,
    setActiveReturnParametersForFactsheet,
  };
}
